import { DATAVERSE_OPERATOR_MAP, type OperatorArity } from "@kodikas/fetchxml-ts";
import type {
  ConditionDraft,
  ConditionGroupDraft,
  QueryEntityMetadata,
  JoinDraft,
  QueryTarget,
} from "../../types.js";

export const LINK_TYPES = ["inner", "outer", "any", "not any", "all", "not all", "exists", "in", "matchfirstrowusingcrossapply"];

function makeId(prefix: string): string {
  return `${prefix}-${Math.random().toString(16).slice(2, 10)}-${Date.now().toString(16)}`;
}

export function newJoinDraft(parentTarget: QueryTarget): JoinDraft {
  return {
    id: makeId("join"),
    parentTarget,
    relationKey: "",
    alias: "",
    linkType: "outer",
    selectedAttributes: [],
    targetEntityLogicalName: "",
    fromAttribute: "",
    toAttribute: "",
  };
}

export function newConditionGroup(target: QueryTarget, parentGroupId: string | null = null): ConditionGroupDraft {
  return {
    id: makeId("group"),
    target,
    parentGroupId,
    type: "and",
  };
}

export function newConditionDraft(groupId: string, target: QueryTarget): ConditionDraft {
  return {
    id: makeId("cond"),
    target,
    groupId,
    attribute: "",
    operator: "eq",
    value: "",
    secondValue: "",
    multiValues: "",
    valueOfAttribute: "",
  };
}

export function buildAlias(targetLogicalName: string, usedAliases: Set<string>): string {
  const parts = targetLogicalName.split("_");
  const base =
    parts
      .filter((part) => part.length > 0)
      .map((part) => part[0].toUpperCase())
      .join("")
      .slice(0, 4) || targetLogicalName.slice(0, 2).toUpperCase();

  if (!usedAliases.has(base)) {
    return base;
  }

  let i = 2;
  while (usedAliases.has(`${base}${i}`)) {
    i += 1;
  }
  return `${base}${i}`;
}

export function valueEditorMode(condition: ConditionDraft): OperatorArity {
  const spec = DATAVERSE_OPERATOR_MAP.get(condition.operator);
  return spec?.arity ?? "single";
}

export function getDescendantGroupIds(groupId: string, groups: ConditionGroupDraft[]): string[] {
  const children = groups.filter((item) => item.parentGroupId === groupId);
  const ids = [groupId];
  for (const child of children) {
    ids.push(...getDescendantGroupIds(child.id, groups));
  }
  return ids;
}

export function getTargetLabel(
  target: QueryTarget,
  rootMetadata: QueryEntityMetadata | null,
  joins: JoinDraft[],
): string {
  if (target === "root") {
    return `Root (${rootMetadata?.logicalName || "entity"})`;
  }

  const join = joins.find((item) => item.id === target);
  if (!join) {
    return `Join (${target})`;
  }
  return `${join.alias || "join"} (${join.targetEntityLogicalName || "unconfigured"})`;
}

export function getTargetMetadata(
  target: QueryTarget,
  rootMetadata: QueryEntityMetadata | null,
  joinMetadataByJoinId: Record<string, QueryEntityMetadata | undefined>,
): QueryEntityMetadata | null {
  if (!rootMetadata) return null;
  if (target === "root") return rootMetadata;
  return joinMetadataByJoinId[target] || null;
}

export function getParentMetadataForJoin(
  join: JoinDraft,
  rootMetadata: QueryEntityMetadata | null,
  joinMetadataByJoinId: Record<string, QueryEntityMetadata | undefined>,
): QueryEntityMetadata | null {
  if (join.parentTarget === "root") {
    return rootMetadata;
  }
  return joinMetadataByJoinId[join.parentTarget] || null;
}
